import { getManualModules, updateManualModule } from './moodleService';
import { draftEvaluationWithAI, extractAssignmentQuestions, QuestionResult } from './adminService';
import { extractTextFromFile } from '../utils/fileExtraction';
import { stripHtml } from '../utils/moduleUtils';

/**
 * Per-participant AI draft generation for the bulk evaluation page (see
 * specs/2026-09-17-bulk-assignment-ai-evaluation.md). Deliberately new,
 * standalone code - reuses the same underlying services
 * TrainerLedView.tsx's single-participant flow already uses
 * (getManualModules, extractTextFromFile, draftEvaluationWithAI), but does
 * not modify or share code with that component, which stays exactly as-is.
 *
 * Known, deliberate scope limit: if this assignment's extractedquestions
 * hasn't already been cached (via the single-participant flow extracting
 * it from a reference document at least once), this falls back to the
 * plain stripped-HTML description rather than triggering that AI
 * extraction step itself - keeps bulk generation to exactly one AI call
 * per participant, matching the spec's "one call per participant" design
 * decision, rather than adding a second, shared-state extraction step.
 */

export interface BulkDraftResult {
    status: 'ok' | 'blocked' | 'error';
    blockedReason?: string;
    draft?: {
        score: number;
        evaluation: string;
        confidence?: 'high' | 'medium' | 'low';
        submissionText: string;
        questionResults?: QuestionResult[];
        possiblyAiWritten?: { reason: string };
    };
}

export interface BulkSubmissionPreview {
    status: 'ok' | 'blocked';
    blockedReason?: string;
    submissionText?: string;
    instructions?: string;
}

const findModule = async (
    courseId: number | string,
    participant: { userid?: number; email?: string },
    assignmentId: number
): Promise<any | null> => {
    const response = await getManualModules(courseId, participant.userid || participant.email);
    const modules: any[] = response?.modules || [];
    return modules.find((m: any) => m.moduleId === String(assignmentId)) || null;
};

// getManualModules() returns the RAW Moodle webservice shape, not the
// mapped `Module` type TrainerLedView.tsx/TrainerLedTrainingDetails.tsx work
// with - field names differ (content vs description, title vs name,
// lowercase evalcriteria/extractedquestions/maxcredits). These two helpers
// apply the exact same mapping TrainerLedTrainingDetails.tsx's loadDetails
// already does, so bulk generation actually gets the real instructions and
// the trainer's criteria checklist instead of silently reading undefined fields.
const getInstructions = (mod: any): string => mod.extractedquestions || stripHtml(mod.content || '');

const getFilledCriteria = (mod: any): { name: string }[] => {
    if (!mod.evalcriteria) return [];
    let parsed: any[];
    try {
        parsed = JSON.parse(mod.evalcriteria) || [];
    } catch {
        return [];
    }
    return parsed.filter((c: any) => c.name && c.name.trim());
};

// Fetches (and, for files, extracts) the participant's submission text -
// stops here, no AI call. Returns a blocked reason for "nothing submitted"
// or "file unreadable" without needing to invoke the AI for either case.
const loadSubmissionText = async (mod: any): Promise<{ status: 'ok' | 'blocked'; blockedReason?: string; answerText?: string }> => {
    const submissionFiles = mod.submissionfiles || [];
    const hasSubmission = !!(mod.answer && mod.answer.trim())
        || submissionFiles.length > 0
        || !!(mod.submissionid && mod.submissionid > 0);
    if (!hasSubmission) {
        return { status: 'blocked', blockedReason: 'No submission to evaluate.' };
    }

    let answerText = mod.answer || '';
    if (submissionFiles.length > 0) {
        const results = await Promise.all(submissionFiles.map((f: any) => extractTextFromFile(f)));
        const firstFailure = results.find((r) => !r.ok);
        if (firstFailure) {
            return {
                status: 'blocked',
                blockedReason: firstFailure.reason === 'scanned-or-empty'
                    ? "One of the files appears to be a scanned/image-only document. Please evaluate manually."
                    : "One of the attached files isn't a supported format. Please evaluate manually.",
            };
        }
        answerText = [answerText, ...results.map((r) => r.text)].filter((t) => t.trim()).join('\n\n');
    }
    return { status: 'ok', answerText };
};

// Lightweight preview for expanding a row before generating - shows the
// submission (and blocks with a reason if it can't be read) without ever
// calling the AI, so simply looking at a row never costs an AI call.
export const fetchSubmissionPreview = async (
    courseId: number | string,
    participant: { userid?: number; email?: string },
    assignmentId: number
): Promise<BulkSubmissionPreview> => {
    const mod = await findModule(courseId, participant, assignmentId);
    if (!mod) {
        return { status: 'blocked', blockedReason: 'No submission to evaluate.' };
    }
    const instructions = getInstructions(mod);
    const submission = await loadSubmissionText(mod);
    if (submission.status !== 'ok') {
        return { status: 'blocked', blockedReason: submission.blockedReason, instructions };
    }
    return { status: 'ok', submissionText: submission.answerText, instructions };
};

// The assignment's question/instructions are the same for every participant
// on this assignment - fetched once, from any one enrolled participant,
// for display at the top of the bulk evaluation page (never per row).
//
// Mirrors TrainerLedView.tsx's handleOpenEvalModal: the real question can
// live in an uploaded reference file rather than the plain description, so
// when nothing is cached yet and a reference file is attached, extract it
// via the same AI endpoint - and cache the result back (updateManualModule),
// the same persistence the single-participant flow does on save. The
// "one AI call per participant, never per-row" scope limit doesn't apply
// here: this runs once per page load, not once per participant, so there's
// no N-participant AI-cost concern to avoid.
export const fetchAssignmentInstructions = async (
    courseId: number | string,
    participant: { userid?: number; email?: string },
    assignmentId: number
): Promise<string | null> => {
    const mod = await findModule(courseId, participant, assignmentId);
    if (!mod) return null;
    if (mod.extractedquestions) return mod.extractedquestions;

    const baseDescription = stripHtml(mod.content || '');
    const introfiles = mod.introfiles || [];
    if (introfiles.length === 0) return baseDescription;

    const refResults = await Promise.all(introfiles.map((f: any) => extractTextFromFile(f)));
    const successfulRefs = refResults.filter((r) => r.ok && r.text.trim());
    if (successfulRefs.length === 0) return baseDescription;

    const combinedRefText = successfulRefs.map((r) => r.text).join('\n\n');
    const aiResult = await extractAssignmentQuestions({ assignmentTitle: mod.title, referenceText: combinedRefText });
    if (!aiResult || !aiResult.found || !aiResult.questions.trim()) return baseDescription;

    const combined = baseDescription ? `${baseDescription}\n\n${aiResult.questions}` : aiResult.questions;
    try {
        await updateManualModule({ assignmentid: assignmentId, extractedquestions: combined });
    } catch {
        // Non-fatal - the page still has the text to show even if caching fails.
    }
    return combined;
};

// The assignment's evaluation criteria are the same for every participant on
// this assignment - fetched once, from any one enrolled participant, for the
// bulk evaluation page's criteria editor (never per row, same reasoning as
// fetchAssignmentInstructions above).
export const fetchAssignmentCriteria = async (
    courseId: number | string,
    participant: { userid?: number; email?: string },
    assignmentId: number
): Promise<{ name: string }[]> => {
    const mod = await findModule(courseId, participant, assignmentId);
    if (!mod) return [];
    return getFilledCriteria(mod);
};

// Mirrors TrainerLedView.tsx's handleSaveEval criteria-persistence logic
// exactly (trim, drop blanks, compare against what's already stored, only
// write if different) - the bulk page has no single per-participant save
// moment to piggyback on, so criteria get their own explicit save action
// using the same underlying call and the same "don't write unless changed"
// rule. Returns whether it actually wrote anything.
export const saveAssignmentCriteria = async (
    assignmentId: number,
    criteria: { name: string }[],
    existingCriteria: { name: string }[]
): Promise<boolean> => {
    const trimmed = criteria
        .filter((c) => c.name && c.name.trim())
        .map((c) => ({ name: c.name.trim() }));
    const changed = JSON.stringify(trimmed) !== JSON.stringify(existingCriteria || []);
    if (!changed) return false;
    await updateManualModule({ assignmentid: assignmentId, evalcriteria: JSON.stringify(trimmed) });
    return true;
};

export const generateBulkEvaluationDraft = async (
    courseId: number | string,
    participant: { userid?: number; email?: string },
    assignmentId: number,
    fallbackMaxCredits: number = 100
): Promise<BulkDraftResult> => {
    const mod = await findModule(courseId, participant, assignmentId);
    if (!mod) {
        return { status: 'blocked', blockedReason: 'No submission to evaluate.' };
    }

    const submission = await loadSubmissionText(mod);
    if (submission.status !== 'ok') {
        return { status: 'blocked', blockedReason: submission.blockedReason };
    }
    const answerText = submission.answerText!;

    const instructions = getInstructions(mod);
    const filledCriteria = getFilledCriteria(mod);
    const maxCredits = mod.maxcredits ?? fallbackMaxCredits;

    const aiResult = await draftEvaluationWithAI({
        assignmentTitle: mod.title,
        instructions,
        answerText,
        maxCredits,
        ...(filledCriteria.length > 0 ? { criteria: filledCriteria } : {}),
    });

    if (!aiResult) {
        return { status: 'error', blockedReason: 'AI draft generation failed.' };
    }

    return {
        status: 'ok',
        draft: {
            score: aiResult.total_score,
            evaluation: aiResult.evaluation,
            confidence: aiResult.overallConfidence,
            submissionText: answerText,
            questionResults: aiResult.question_results || [],
            ...(aiResult.possibly_ai_written ? { possiblyAiWritten: aiResult.possibly_ai_written } : {}),
        },
    };
};
